import * as jmespath from 'jmespath';
import { ChangeDetectionStrategy, Component, Input, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs/Subject';
import { cloneDeep as _cloneDeep, get, isEmpty, isNil } from 'lodash';
import {take, takeUntil} from 'rxjs/operators';
import { ConditionalItem, IConditionalItem } from '@ispm/shared/classes/conditional-item.class';
import { ConfigurationStoreHandler } from '../../../../configuration/services/configuration-store-handler.service';
import { DynamicFormItem } from '@ispm/dynamic-form/classes/dynamic-form-item.class';
import { FormHandlerService } from '@ispm/dynamic-form/services/form-handler.service';
import { ITEMS_TYPES } from '@ispm/dynamic-form/constants/ItemsTypes';
import { IWidgetItem } from '@ispm/widgets/interfaces/widget-item.interface';
import { IdynamicFormItem } from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import { ResolvableComponent } from '@ispm/core/decorators/resolvable-component.decorator';
import { TabsHeaderComponent } from '@ispm/layout/components/tabs-header/tabs-header.component';
import { TherapiesActionCreatorService } from '@ispm/therapies/services/therapies-action-creator.service';
import { TranslationPipe } from '@ispm/shared/pipes/string-translate.pipe';
import {ActivatedRoute} from "@angular/router";
import {RouterManagerService} from "@ispm/core/services/router-manager.service";

export interface DynamicFormTabsConf {
  tabs: DynamicFormTabConf[];
  tabsHeaderCss?: string;
  forms?: string[];
  initialSelectionByDataBinding?: any;
}

export interface DynamicFormTabHeaderConf {
  title: string;
  rootPath?: string;
  rootPaths?: string[];
  selected?: boolean;
  onTabSelected?: any;
  displayConditions?: Array<IConditionalItem>;
  groupingIdentifier?: {
    path: string
  };
}

export interface DynamicFormTabConf {
  header: DynamicFormTabHeaderConf;
  displayConditions?: Array<IConditionalItem>;
  content: { formId: string, formItemFullWidth: boolean };
}

export interface TabContent {
  name: string;
  formId: string;
  formFullWidth: boolean;
  contentLength?: number;
  groupingIdentifier?: { path?: string };
}

@Component({
  selector: 'ispm-dynamic-drawers',
  templateUrl: './dynamic-form-drawers.component.html',
  styleUrls: ['./dynamic-form-drawers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TranslationPipe]
})
@ResolvableComponent(null, 'DynamicFormDrawers')
export class DynamicFormDrawersComponent implements IWidgetItem, OnInit, OnDestroy {

  @Input()
  public additionalSearchValues: any;

  @Input()
  public tabsHeaderCss: string = '';

  public set widgetData(data) {
    if (!isEmpty(data)) {
      this._widgetData = _cloneDeep(data);
      this.setDrawerGroups();
      this.setCountersAtTabsHeader();
      this.cd.markForCheck();
    }
  }

  public get widgetData() {
    return this._widgetData;
  }

  public set widgetConfig(widgetConfig: IdynamicFormItem<DynamicFormTabsConf>) {
    this._widgetConfig = widgetConfig;
    Object.assign(this, get(widgetConfig, 'widgetPayload', { }));
    this.preFetchForms();
    this.tabContent = widgetConfig.widgetPayload.tabs.map(tab => (
      {
        name: tab.header.title,
        formId: tab.content.formId,
        formFullWidth: isNil(tab.content.formItemFullWidth) ? true : tab.content.formItemFullWidth,
        groupingIdentifier: tab.header.groupingIdentifier
      }
    ));
    this.setDrawerGroups();
    this.setCountersAtTabsHeader();
    this.tabsHeaderCss = isNil(widgetConfig.widgetPayload.tabsHeaderCss) ? '' : widgetConfig.widgetPayload.tabsHeaderCss;
    this.cd.markForCheck();
  }

  public get widgetConfig(): IdynamicFormItem<DynamicFormTabsConf> {
    return this._widgetConfig;
  }

  @ViewChild(TabsHeaderComponent, { static: false }) public tabsHeader: TabsHeaderComponent;
  public tabContent: TabContent[] = [];
  public tabsData: { [key: string]: Array<any> } = { };
  public loadedTabs: Set<string> = new Set();
  public selectedTabId: { [id: string]: boolean} = { };
  public _widgetData: any;
  public widgetUpdateValueCallback: (value) => void;
  public widgetFormInstance: FormGroup;
  public addWidgetFormControl: (config: IdynamicFormItem) => void;
  public forceUpdate: (value) => void;

  private _widgetConfig: IdynamicFormItem<DynamicFormTabsConf>;
  private readonly revisionId;
  private destroyer$: Subject<void> = new Subject<void>();

  public constructor(
    private cd: ChangeDetectorRef,
    private formHandlerService: FormHandlerService,
    private translationPipe: TranslationPipe,
    private configurationAC: ConfigurationStoreHandler,
    private therapyAC: TherapiesActionCreatorService,
    private routerManagerService: RouterManagerService,
  ) {
    this.revisionId = this.routerManagerService.parameters.revisionId;
  }

  public ngOnInit(): void {
    this.setInitSelection();
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  public setDrawerGroups() {
    const tabsConf = this._widgetConfig.widgetPayload.tabs;

    for (let i = 0; i < tabsConf.length; i++) {
      const tabHeader = tabsConf[i].header;
      const displayConditions = tabHeader.displayConditions;
      let tabData = [];

      if (tabsConf[i].header.rootPath) {
         tabData = jmespath.search(this._widgetData, tabsConf[i].header.rootPath);
      } else if (Array.isArray(tabsConf[i].header.rootPaths)) {
        tabsConf[i].header.rootPaths.forEach(path => {
          const jmesPathCalculation = jmespath.search(this._widgetData, path.replace('\[revisionId\]', this.revisionId));

          if (jmesPathCalculation) {
            tabData = [...tabData, ...jmesPathCalculation];
          } else {
            tabData = [...tabData];
          }
        });
      }

      if (Array.isArray(tabData) && Array.isArray(displayConditions)) {
        tabData = tabData.filter((item) =>
          displayConditions.every(condition =>
            ConditionalItem.builder(item, condition.value, condition.operator, condition.path).verify()
          )
        );
      }

      this.tabsData[tabsConf[i].content.formId] = tabData;
    }
  }

  public getDrawerName(tab) {
    return tab.contentLength >= 0
      ? `${this.translationPipe.transform(tab.name)} (${tab.contentLength})`
      : `${this.translationPipe.transform(tab.name)}`
    ;
  }

  public tabCommunicates($event: any) {
    this.bubbleUpUpdate($event.form);
  }

  public trackByFn(index, item) {
    return index + '-' + item['id'];
  }

  public trackById(index, item) {
    return get(item, 'id', index) + '_' + get(item, 'meta.versionId', index); // for handling edit cases
  }

  public selectedChange(open: boolean, tabFormId) {
    if (open) {
      this.formHandlerService.accordionSelectionChanged$.next({itemId: tabFormId, open: open});
      this.loadedTabs.add(tabFormId);
    }
    this.selectedTabId[tabFormId] = open;
    this.cd.markForCheck();
  }

  public onTabToggle(event: any, open: boolean = false) {
  }

  private bubbleUpUpdate(updates: any) {
    setTimeout(function () {
      if (isEmpty(this.widgetFormInstance.controls['_tabActiveRecordMeta'])) {
        this.addWidgetFormControl(DynamicFormItem.builder('', '_tabActiveRecordMeta', ITEMS_TYPES.HIDDEN).setDefault(null));
      }

      this.widgetFormInstance.controls['_tabActiveRecordMeta'].patchValue(updates);
      this.widgetUpdateValueCallback({ ...this.widgetFormInstance.value[this._widgetConfig.name] });
    }.bind(this), 0);
  }

  private preFetchForms() {
    const forms = this._widgetConfig.widgetPayload.forms;

    if (forms) {
      forms.forEach(formId => this.formHandlerService.getForm(formId));
    }
  }

  private setInitSelection() {
    const selectionDataBinding = this._widgetConfig.widgetPayload.initialSelectionByDataBinding;

    if (isEmpty(selectionDataBinding)) {
      this.getSelectionFromConfig().forEach(this.loadedTabs.add, this.loadedTabs);

      if (this.loadedTabs.size) {
        this.selectedTabId[this.loadedTabs.values().next().value] = true;
        this.cd.markForCheck();
      }
    }
  }

  private getSelectionFromConfig(): Array<string> {
    let selectedTabsAtInit = [];
    const tabsConf = this._widgetConfig.widgetPayload.tabs;
    const selectedTabs = tabsConf.filter(tab => tab.header.selected);

    if (selectedTabs.length) {
      selectedTabsAtInit = selectedTabs.map(tab => tab.content.formId);
    }

    return selectedTabsAtInit;
  }

  private setCountersAtTabsHeader() {
    const subjectIds = [];

    this.tabContent.forEach((tab: TabContent) => {
      if (Array.isArray(this.tabsData[tab.formId])) {
        tab.contentLength = this.tabsData[tab.formId].length;
        subjectIds[tab.formId] = new Set();

        if (tab.groupingIdentifier && tab.groupingIdentifier.path) {
          const groupSet = new Set();

          this.tabsData[tab.formId].forEach(tabDataItem => {
            const resultData = jmespath.search(tabDataItem, tab.groupingIdentifier.path);
            groupSet.add(resultData);
            subjectIds[tab.formId].add(resultData);
          });

          tab.contentLength = groupSet.size;
        } else {
          this.tabsData[tab.formId].forEach(subject => {
            if (subject && subject.id) {
              subjectIds[tab.formId].add(subject.id);
            }
          });
        }
      } else {
        tab.contentLength = -1;
      }
    });

    this.setDefaultBehaviorVisibleInReport(subjectIds);
  }

  /**
   * Method for the feature GEN-85860
   *
   * @param subjects
   * @private
   */
  private setDefaultBehaviorVisibleInReport(subjectIds) {
    let viewInReportSettings;

    this.configurationAC.getCurrentConfig()
      .pipe(
        takeUntil(this.destroyer$.asObservable()),
      )
      .subscribe((testConfiguration: any) => {
        viewInReportSettings = get(
          testConfiguration,
          'TreatmentLayout.subject_selection_section.formConf.defaultViewInReport',
          []
        );
      });

    if (viewInReportSettings) {
      let includeOpinions;

      this.therapyAC.getIncludedMap()
        .pipe(
          takeUntil(this.destroyer$.asObservable())
        )
        .subscribe(includedMap => {
          viewInReportSettings.forEach(card => {
            for (const i in card) {
              if (card.hasOwnProperty(i)) {
                subjectIds[i].forEach(subject => {
                  if (get(includedMap.report, subject, false) === false && card[i].default === false) {
                    this.therapyAC.updateInclusionOpinion('report', subject, card[i].default).pipe(take(1)).subscribe();
                  }
                });
              }
            }
          });
        });
    }
  }

}

